import { Form, Input, Switch } from 'antd';
import {
  FormAttachment,
  FormSelect,
  FormTextArea,
  FormTitle,
  FormNumber,
  FormCheckbox,
} from '../form/FormBasic';
import { useEffect, useState, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  maxLengthRule,
  minLengthRule,
  noSpacesOnlyRule,
  regexRule,
  requiredRule,
} from '../../helpers/formHelper';
import { referenceDataSyncService } from '../../services/referenceDataSyncService';
import { FileTypeEnum } from '../../enums/FileTypeEnum';
import FormSelectCustomTwoRowPerOption from '../form/FormSelectCustomTwoRowPerOption';
import { useForm, useWatch } from 'antd/es/form/Form';
import AddProductForm from '../product/AddProduct';
import { Plus, XSquare, QrCodeScan, PencilSquare } from 'react-bootstrap-icons';
import { Modal } from 'antd';
import { PriceStatusEnum } from '../../enums/PriceStatusEnum';
import BarcodeScanner from '../common/BarcodeScanner';
import PrimaryButton from '../buttons/PrimaryButton';
import { useNotificationContext } from '../../contexts/NotificationContext';
import FormModelComponent from '../model/FormModelComponent';
import PriceProductCategoryCard from '../../pages/home/components/PriceProductCategoryCard';
import { PermissionEnum } from '../../enums/PermissionEnum';
import { usePermissions } from '../../hooks/usePermissions';
import { usePresentationFormOptions } from '../../hooks/usePresentationFormOptions';
import { PresentationStatusEnum } from '../../enums/PresentationStatusEnum';
import { REGEX_ALL_ALLOWED_SAFE_FILTER } from '../../constants/regex';
import { useMediaQuery } from 'react-responsive';
import RESOURCE_CONFIGS from '../../constants/resourceConfig';
import { RoleName } from '../../enums/RoleNameEnm';
import { normalizePhotoValue } from '../../utils/normalizePhotoValue';
import { PriceAddPresentationFormProps } from '../../types/priceTypes';

const PriceAddPresentationForm: React.FC<PriceAddPresentationFormProps> = ({
  form,
  addPresentationToList,
  resetPresentationSection = 0,
  triggeredFromTask,
  taskData,
  presentationFromTask,
  isActive,
  onDelegatedToggle,
  onNewProductCreated,
}) => {
  const { t } = useTranslation(['price', 'form', 'product']);

  const isMobile = useMediaQuery({
    query: `(max-width: ${RESOURCE_CONFIGS.RESPONSIVE_BREAKPOINTS.SM})`,
  });

  const [selectedBaskets, setSelectedBaskets] = useState<string[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<number[]>([]);
  const [addProductForm] = useForm();

  const {
    mainProductOptions,
    enrichedProductsOptions,
    selectedProduct,
    setSelectedProduct,
    showSelectedProductSection,
    setShowSelectedProductSection,
    isProductModalOpen,
    isScannerModalOpen,
    setIsScannerModalOpen,
    hasCustomFields,
    onScanSuccess,
    onSearch,
    handleNewProductButton,
    closeNewProductModal,
    handleCancel,
    handleNewProductAddition,
    autoSelectedNewProduct,
    handlescanFailure,
    loadingMainProducts,
    loadingPresentations,
  } = usePresentationFormOptions({
    form,
    triggeredFromTask,
    productFromTask: presentationFromTask,
    status: PresentationStatusEnum.APPROVED,
    resetProductSection: resetPresentationSection,
    selectedCategories,
    setSelectedCategories,
    addProductForm,
    onSuccess: onNewProductCreated,
  });

  const priceVal = useWatch('price', form);
  const isNotFoundVal = useWatch('isNotFound', form);
  const productRemarksVal = useWatch('productRemarks', form);
  const { successNotification, errorNotification } = useNotificationContext();
  const [hasErrors, setHasErrors] = useState(false);

  // Refs to prevent infinite loops
  const isUpdatingBasketsRef = useRef(false);
  const isUpdatingCategoriesRef = useRef(false);

  const [basketData, setBasketData] = useState<any>();
  const [categoryData, setCategoryData] = useState<any>();
  const [categorySearchTerm] = useState<string>('');

  const photoOrInvoiceConvertedValue = useWatch(`photoOrInvoiceConverted`, form);

  const initialPhotos = useMemo(() => {
    if (presentationFromTask?.isDelegated || isNotFoundVal) {
      return [];
    }
    if (triggeredFromTask) {
      if (presentationFromTask?.priceData?.photoOrInvoice) {
        return normalizePhotoValue(presentationFromTask.priceData.photoOrInvoice);
      }
      return [];
    }
    return normalizePhotoValue(photoOrInvoiceConvertedValue);
  }, [
    presentationFromTask?.isDelegated,
    isNotFoundVal,
    triggeredFromTask,
    presentationFromTask?.priceData?.photoOrInvoice,
    photoOrInvoiceConvertedValue,
  ]);

  const { hasPermission, hasRoles } = usePermissions();

  const hasAddNewProductPermissions =
    hasRoles([
      RoleName.INSPECTOR,
      RoleName.AUDITOR,
      RoleName.ADMIN,
      RoleName.SYSTEM_ADMIN,
      RoleName.ANALYST,
    ]) && hasPermission(PermissionEnum.CREATE_PRESENTATION);

  const getBaskets = async () => {
    const basketsData: any[] = await referenceDataSyncService.getBaskets();
    const basketDataWithSelection = basketsData.map((basket) => ({
      ...basket,
      isSelected: selectedBaskets.includes(basket?.refId),
    }));
    isUpdatingBasketsRef.current = true;
    setBasketData(basketDataWithSelection);
    setTimeout(() => {
      isUpdatingBasketsRef.current = false;
    }, 0);
    return basketDataWithSelection;
  };

  const getProductCategories = async () => {
    const categoriesData = await referenceDataSyncService.getProductCategories();
    const categoryDataWithSelection = categoriesData.map((category) => ({
      ...category,
      isSelected: selectedCategories.includes(category.id),
    }));
    isUpdatingCategoriesRef.current = true;
    setCategoryData(categoryDataWithSelection);
    setTimeout(() => {
      isUpdatingCategoriesRef.current = false;
    }, 0);
    return categoryDataWithSelection;
  };

  const resetBasketSelection = () => {
    isUpdatingBasketsRef.current = true;
    isUpdatingCategoriesRef.current = true;

    const updatedBasket = basketData.map((basket: { refId: string }) => ({
      ...basket,
      isSelected: false,
    }));
    const updatedCategory = categoryData.map((category: { id: number }) => ({
      ...category,
      isSelected: false,
    }));
    setBasketData(updatedBasket);
    setCategoryData(updatedCategory);
    setSelectedBaskets([]);
    setSelectedCategories([]);

    // Reset flags after state updates
    setTimeout(() => {
      isUpdatingBasketsRef.current = false;
      isUpdatingCategoriesRef.current = false;
    }, 0);
  };

  useEffect(() => {
    const initializeData = async () => {
      await getBaskets();
      await getProductCategories();
    };
    initializeData();
  }, []);

  useEffect(() => {
    if (triggeredFromTask && taskData) {
      const performAutoSelection = async () => {
        if (taskData.basketRefIds && taskData.basketRefIds.length > 0) {
          const newSelectedBaskets = [...taskData.basketRefIds];
          setSelectedBaskets(newSelectedBaskets);
        }

        if (taskData.productCategoryIds && taskData.productCategoryIds.length > 0) {
          const newSelectedCategories = [...taskData.productCategoryIds];
          setSelectedCategories(newSelectedCategories);
        }
      };

      performAutoSelection();
    }
  }, [triggeredFromTask, taskData]);

  const [isEditingPrice, setIsEditingPrice] = useState<boolean>(false);
  const [isDelegatedState, setIsDelegatedState] = useState<boolean>(
    !!presentationFromTask?.isDelegated
  );

  useEffect(() => {
    setIsDelegatedState(!!presentationFromTask?.isDelegated);
  }, [presentationFromTask?.isDelegated]);

  useEffect(() => {
    if (triggeredFromTask && presentationFromTask && isActive) {
      const hasAddedPrice = presentationFromTask?.priceAdded || !!presentationFromTask?.priceData;
      const existingPriceData = presentationFromTask?.priceData;

      if (hasAddedPrice && existingPriceData) {
        const isNotFound = existingPriceData.status === PriceStatusEnum.PRODUCTNOTFOUND;
        const formattedPhoto = normalizePhotoValue(existingPriceData.photoOrInvoice);
        form.setFieldsValue({
          price: isNotFound || existingPriceData.price === 0 ? undefined : existingPriceData.price,
          isNotFound: isNotFound,
          productRemarks: existingPriceData.productRemarks,
          photoOrInvoice: formattedPhoto,
          photoOrInvoiceConverted: formattedPhoto,
          productRefId: presentationFromTask?.refId,
          productName: presentationFromTask?.name,
        });
        setIsEditingPrice(false);
      } else {
        form.resetFields([
          'price',
          'isNotFound',
          'productRemarks',
          'photoOrInvoice',
          'photoOrInvoiceConverted',
        ]);
        form.setFieldsValue({
          price: undefined,
          isNotFound: false,
          productRemarks: undefined,
          photoOrInvoice: undefined,
          photoOrInvoiceConverted: undefined,
          productRefId: presentationFromTask?.refId,
          productName: presentationFromTask?.name,
        });
        setIsEditingPrice(true);
      }
    }
  }, [triggeredFromTask, presentationFromTask, isActive, form]);

  useEffect(() => {
    if (basketData && basketData.length > 0 && !isUpdatingBasketsRef.current) {
      const needsUpdate = basketData.some(
        (basket: { refId: string; isSelected: boolean }) =>
          basket.isSelected !== selectedBaskets.includes(basket?.refId)
      );

      if (needsUpdate) {
        isUpdatingBasketsRef.current = true;
        const updatedBasket = basketData.map((basket: { refId: string }) => ({
          ...basket,
          isSelected: selectedBaskets.includes(basket?.refId),
        }));
        setBasketData(updatedBasket);
        // Reset the flag after state update
        setTimeout(() => {
          isUpdatingBasketsRef.current = false;
        }, 0);
      }
    }
  }, [selectedBaskets]);

  useEffect(() => {
    if (categoryData && categoryData.length > 0 && !isUpdatingCategoriesRef.current) {
      const needsUpdate = categoryData.some(
        (category: { id: number; isSelected: boolean }) =>
          category.isSelected !== selectedCategories.includes(category?.id)
      );

      if (needsUpdate) {
        isUpdatingCategoriesRef.current = true;
        const updatedCategory = categoryData.map((category: { id: number }) => ({
          ...category,
          isSelected: selectedCategories.includes(category?.id),
        }));
        setCategoryData(updatedCategory);
        // Reset the flag after state update
        setTimeout(() => {
          isUpdatingCategoriesRef.current = false;
        }, 0);
      }
    }
  }, [selectedCategories]);

  const handleCategoryClick = async (id: number, mainCategory: string) => {
    if (id) {
      const currentCategoryData = await referenceDataSyncService.getProductCategories();
      const mainCategoryIds =
        currentCategoryData
          ?.filter((cat: any) => cat.mainCategory === mainCategory)
          ?.map((cat: any) => cat.id) || [];

      const isCurrentlySelected = mainCategoryIds.some((catId: number) =>
        selectedCategories.includes(catId)
      );

      let newSelectedCategories: number[];

      if (isCurrentlySelected) {
        newSelectedCategories = selectedCategories.filter(
          (categoryId) => !mainCategoryIds.includes(categoryId)
        );
      } else {
        newSelectedCategories = [...selectedCategories, ...mainCategoryIds];
      }

      setSelectedCategories(newSelectedCategories);
    }

    form.setFieldValue('mainProductName', undefined);
    form.setFieldValue('productSearch', undefined);
    setSelectedProduct([]);
    setShowSelectedProductSection(false);
  };

  const handleNotFoundChange = (e: any) => {
    if (e.target.checked) {
      form.setFieldValue('price', undefined);
      form.setFieldsValue({
        photoOrInvoice: [],
        photoOrInvoiceConverted: [],
      });
    }
  };

  const handleDelegatedToggleChange = (checked: boolean) => {
    setIsDelegatedState(checked);
    if (checked) {
      form.setFieldsValue({
        price: undefined,
        isNotFound: false,
        photoOrInvoice: [],
        photoOrInvoiceConverted: [],
        productRemarks: undefined,
      });
    }
    const targetRefId =
      presentationFromTask?.refId || presentationFromTask?.productRefId || presentationFromTask?.id;
    if (onDelegatedToggle && targetRefId) {
      onDelegatedToggle(targetRefId, checked);
    }
  };

  const handleAddPresentationToList = () => {
    if (triggeredFromTask) {
      form.setFieldsValue({
        productRefId: presentationFromTask?.refId,
        productName: presentationFromTask?.name,
      });
    }
    addPresentationToList();
    resetBasketSelection();
  };

  const filteredBaskets = basketData || [];

  // Group categories by mainCategory and only show unique main categories
  const filteredCategories =
    categoryData
      ?.filter((category: any) =>
        category?.mainCategory?.toLowerCase().includes(categorySearchTerm.toLowerCase())
      )
      ?.reduce((acc: any[], category: any) => {
        const existingMainCategory = acc.find(
          (item) => item.mainCategory === category.mainCategory
        );
        if (!existingMainCategory) {
          acc.push({
            id: category.id,
            name: category.mainCategory,
            mainCategory: category.mainCategory,
            isSelected: selectedCategories.some(
              (catId) =>
                categoryData.find((cat: any) => cat.id === catId)?.mainCategory ===
                category.mainCategory
            ),
          });
        }
        return acc;
      }, []) || [];

  const hasAllFieldsValue =
    (isNotFoundVal || !!priceVal) && (!productRemarksVal || productRemarksVal?.length < 256);

  return (
    <>
      <FormModelComponent
        isOpen={isProductModalOpen}
        header={t('product:createProductTitle')}
        subheader={t('product:createProductSubTitle')}
        content={
          <AddProductForm
            form={addProductForm}
            handleCancel={handleCancel}
            getConfirmation={handleNewProductAddition}
            isModal={true}
            isEdit={false}
          />
        }
        onClickedCancel={closeNewProductModal}
        footerAction={{
          confimation: {
            label: t('product:createProduct'),
            onClick: handleNewProductAddition,
          },
          cancel: {
            label: t('cancel'),
            onClick: handleCancel,
          },
        }}
        closeIcon={<XSquare className="text-primary-font-red" size={20} />}
        contentStyles="mb-4 px-2 max-h-[60vh] md:max-h-[70vh] overflow-y-auto space-y-2"
      />

      <Modal
        title={t('Scan Barcode')}
        open={isScannerModalOpen}
        onCancel={() => setIsScannerModalOpen(false)}
        footer={null}
        destroyOnClose
      >
        <BarcodeScanner
          onScanSuccess={onScanSuccess}
          onScanFailure={handlescanFailure}
          scannerId="reader"
          errorNotification={errorNotification}
        />
      </Modal>

      {!triggeredFromTask && (
        <>
          <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <FormTitle
              label={t('addPresentationHeading')}
              styles="text-lg font-bold text-black md:text-xl mb-0"
            />

            <div className="flex w-full flex-col gap-3 sm:flex-row lg:w-auto">
              <div className="w-full sm:flex-1 lg:w-48">
                <PrimaryButton
                  handleOnClick={() => setIsScannerModalOpen(true)}
                  hideBgGradient={true}
                  type="button"
                  height="40px"
                  width="100%"
                  className="flex items-center justify-center px-6"
                >
                  <div className="flex items-center justify-center gap-2">
                    <QrCodeScan className="h-5 w-5" />
                    <span className="whitespace-nowrap text-sm leading-none md:text-base">
                      {t('Scan')}
                    </span>
                  </div>
                </PrimaryButton>
              </div>

              {hasAddNewProductPermissions && (
                <div className="w-full sm:flex-1 lg:w-48">
                  <PrimaryButton
                    handleOnClick={handleNewProductButton}
                    hideBgGradient={true}
                    type="button"
                    height="40px"
                    width="100%"
                    className="flex items-center justify-center px-6"
                  >
                    <div className="flex items-center justify-center gap-2">
                      <Plus className="h-5 w-5" />
                      <span className="whitespace-nowrap text-sm leading-none md:text-base">
                        {t('product:newProduct')}
                      </span>
                    </div>
                  </PrimaryButton>
                </div>
              )}
            </div>
          </div>

          <div className="mb-2 mt-4">
            <label className="text-md font-medium text-gray-800">{t('productCategories')}</label>
          </div>

          <div className="my-4 grid max-h-80 grid-cols-1 gap-4 overflow-auto lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
            {filteredCategories?.map((category: any) => (
              <PriceProductCategoryCard
                key={category?.id}
                name={category?.name}
                id={category?.id}
                mainCategory={category?.mainCategory}
                category=""
                subCategory=""
                handleCategoryClick={(id: number, _name: string) =>
                  handleCategoryClick(id, category?.mainCategory)
                }
                isSelected={category?.isSelected}
              />
            ))}
          </div>
        </>
      )}

      <Form
        form={form}
        name="addProductDataForm"
        layout="vertical"
        onFinish={handleAddPresentationToList}
        onFieldsChange={(_, allFields) => {
          const errors = allFields.some((field) => field.errors && field.errors.length > 0);
          setHasErrors(errors);
        }}
      >
        {!triggeredFromTask && (
          <div className="mb-4 flex flex-col gap-4 md:flex-row md:items-start">
            <div className="w-full md:flex-1">
              {/* Main Product */}
              <FormSelect
                name="mainProductName"
                options={mainProductOptions}
                label={t('product')}
                placeholder={t('form:dropdownPlaceholder', { field: t('product') })}
                isRequired={false}
                allowClear={true}
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
                }
                loading={loadingMainProducts}
              />
            </div>

            <div className="w-full md:flex-1">
              <Form.Item name="productSearch" label={t('presentationFinder')}>
                <FormSelectCustomTwoRowPerOption
                  placeHolder={t('presentationFindFieldPlaceholder')}
                  options={enrichedProductsOptions}
                  onSearchSelected={onSearch}
                  autoSelectedLabel={autoSelectedNewProduct}
                  loading={loadingPresentations}
                />
              </Form.Item>
            </div>
          </div>
        )}

        {(showSelectedProductSection || triggeredFromTask) && (
          <div
            className={`${triggeredFromTask ? 'p-1 md:p-4' : 'rounded-lg border border-gray-200 p-2 md:p-4'}`}
          >
            <h2 className="text-md font-bold">
              {!triggeredFromTask
                ? t('product:presentationDetails')
                : `${t('presentationLabel')} : ${presentationFromTask?.name} `}
            </h2>
            <div className="my-4 grid grid-cols-1 justify-between gap-4 rounded-lg bg-gray-100 p-4 md:grid-cols-4 lg:flex-row">
              <div>
                <p className="font-bold">{t('nameLabel')}:</p>
                <p>{triggeredFromTask ? presentationFromTask?.name : selectedProduct[0]?.name}</p>
              </div>
              <div>
                <p className="font-bold">{t('unitLabel')}:</p>
                <p>{triggeredFromTask ? presentationFromTask?.unit : selectedProduct[0]?.unit}</p>
              </div>
              <div>
                <p className="font-bold">{t('presentationLabel')}:</p>
                <p>
                  {triggeredFromTask
                    ? presentationFromTask?.presentation || 'N/A'
                    : selectedProduct[0]?.presentation || 'N/A'}
                </p>
              </div>
              <div>
                <p className="font-bold">{t('brandLabel')}:</p>
                <p>
                  {triggeredFromTask
                    ? presentationFromTask?.brand || 'N/A'
                    : selectedProduct[0]?.brand || 'N/A'}
                </p>
              </div>

              {hasCustomFields && presentationFromTask?.customFields
                ? presentationFromTask.customFieldsMetadata.map(
                    (field: { value: any; fieldName: string }, idx: number) => {
                      return (
                        <div key={idx}>
                          <p className="font-bold">{field.fieldName}:</p>
                          <p>{field.value ?? t('notGiven')}</p>
                        </div>
                      );
                    }
                  )
                : null}

              {hasCustomFields && selectedProduct[0]?.customFields
                ? selectedProduct[0].customFieldsMetadata.map(
                    (field: { value: any; fieldName: string }, idx: number) => {
                      return (
                        <div key={idx}>
                          <p className="font-bold">{field.fieldName}:</p>
                          <p>{field.value ?? t('notGiven')}</p>
                        </div>
                      );
                    }
                  )
                : null}
            </div>

            {triggeredFromTask && hasAddNewProductPermissions && (
              <div className="mb-4 w-full sm:w-auto">
                <PrimaryButton
                  handleOnClick={handleNewProductButton}
                  hideBgGradient={true}
                  type="button"
                  height="40px"
                  width={isMobile ? '100%' : 'auto'}
                  className="flex items-center justify-center px-4"
                >
                  <div className="flex items-center justify-center gap-2">
                    <Plus className="h-5 w-5" />
                    <span className="whitespace-nowrap text-sm leading-none md:text-base">
                      {t('product:newProduct')}
                    </span>
                  </div>
                </PrimaryButton>
              </div>
            )}

            <div className="mb-4 flex flex-row flex-wrap items-center gap-4 sm:gap-6">
              {triggeredFromTask && (
                <div className="flex items-center gap-2">
                  <Switch
                    checked={isDelegatedState}
                    disabled={
                      (presentationFromTask?.priceAdded || !!presentationFromTask?.priceData) &&
                      !isEditingPrice
                    }
                    onChange={handleDelegatedToggleChange}
                  />
                  <span className="select-none text-xs font-semibold text-gray-800 sm:text-sm">
                    {t('markAsDelegated')}
                  </span>
                </div>
              )}

              <div className="flex items-center [&_.ant-form-item]:!mb-0">
                <FormCheckbox
                  name="isNotFound"
                  label={t('priceNotFound')}
                  disabled={
                    isDelegatedState ||
                    (triggeredFromTask &&
                      (presentationFromTask?.priceAdded || !!presentationFromTask?.priceData) &&
                      !isEditingPrice)
                  }
                  onChange={handleNotFoundChange}
                />
              </div>
            </div>

            <div>
              <FormNumber
                name="price"
                label={t('price')}
                placeholder={t('priceFieldPlaceholder')}
                isRequired={!isNotFoundVal && !isDelegatedState}
                disabled={
                  isDelegatedState ||
                  isNotFoundVal ||
                  (triggeredFromTask &&
                    (presentationFromTask?.priceAdded || !!presentationFromTask?.priceData) &&
                    !isEditingPrice)
                }
                addonAfter={
                  triggeredFromTask &&
                  (presentationFromTask?.priceAdded || !!presentationFromTask?.priceData) ? (
                    <button
                      type="button"
                      className={`flex cursor-pointer items-center justify-center rounded p-1.5 shadow-sm transition-colors focus:outline-none ${
                        isEditingPrice
                          ? 'border border-gray-300 bg-gray-200 text-gray-400'
                          : 'hover:bg-primary-dark bg-primary text-white'
                      }`}
                      onClick={() => setIsEditingPrice((prev) => !prev)}
                    >
                      <PencilSquare className="h-4 w-4" />
                    </button>
                  ) : undefined
                }
                rules={
                  isNotFoundVal || isDelegatedState
                    ? []
                    : [
                        {
                          required: true,
                          message: t('priceFieldRequired'),
                        },
                      ]
                }
                min="0.01"
                max="99999999"
                type="float"
                precision={2}
              />

              <FormAttachment
                key={
                  isDelegatedState
                    ? 'attachment-delegated'
                    : isNotFoundVal
                      ? 'attachment-not-found'
                      : 'attachment-normal'
                }
                name="photoOrInvoice"
                label={t('priceEvidence')}
                description={t('priceEvidenceDescription')}
                isRequired={false}
                maxCount={5}
                form={form}
                fileTypes={[FileTypeEnum.PNG, FileTypeEnum.JPEG, FileTypeEnum.JPG]}
                initialFiles={initialPhotos}
                isDisabled={
                  isDelegatedState ||
                  isNotFoundVal ||
                  (triggeredFromTask &&
                    (presentationFromTask?.priceAdded || !!presentationFromTask?.priceData) &&
                    !isEditingPrice)
                }
              />

              <FormTextArea
                name="productRemarks"
                label={t('productRemarks')}
                placeholder={t('RespondentObservationsFieldPlaceholder')}
                disabled={
                  isDelegatedState ||
                  (triggeredFromTask &&
                    (presentationFromTask?.priceAdded || !!presentationFromTask?.priceData) &&
                    !isEditingPrice)
                }
                isRequired={false}
                maxLength={200}
                useCustomRules={true}
                rules={[
                  regexRule(t, REGEX_ALL_ALLOWED_SAFE_FILTER),
                  noSpacesOnlyRule(t, t('productRemarks')),
                  minLengthRule(t, t('productRemarks'), 3),
                  maxLengthRule(t, t('productRemarks'), 200),
                ]}
              />
            </div>
            <Form.Item name="productName" label="Product Name" hidden>
              <Input hidden />
            </Form.Item>
            <Form.Item name="productRefId" label="Product Ref Id" hidden>
              <Input hidden />
            </Form.Item>
            <Form.Item name="photoOrInvoiceConverted" label="photoOrInvoiceConverted Value" hidden>
              <Input hidden />
            </Form.Item>

            <PrimaryButton
              handleOnClick={handleAddPresentationToList}
              disabled={
                isDelegatedState ||
                !hasAllFieldsValue ||
                hasErrors ||
                (triggeredFromTask &&
                  (presentationFromTask?.priceAdded || !!presentationFromTask?.priceData) &&
                  !isEditingPrice)
              }
              width="100%"
              height="40px"
              hideBgGradient={true}
              className="mt-2 flex items-center justify-center"
            >
              {t('addPresentationToList')}
            </PrimaryButton>
          </div>
        )}
      </Form>
    </>
  );
};

export default PriceAddPresentationForm;
